'use client';

import { useTranslations } from 'next-intl';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';

// Import promotion images
import bannerImage2 from '@ui/assets/images/become_a_pro_2.png';
import bannerImage3 from '@ui/assets/images/become_a_pro_3.png';
import { ConfirmDialog } from '@ui/components/common/dialog';
import { PromotionSection } from '@ui/sections/promotion/PromotionSection';
import { useBecomeMentor } from '@website/hooks/useBecomeMentor';
import { useCurrentUser } from '@website/hooks/useCurrentUser';
import { MENTOR_LANDING_PATHS } from '@website/lib/seo/routes';
import type { SiteLocale } from '@website/lib/seo/site';

// OnboardingMentor is a 350-line inline-SVG icon (~15 KiB) used only inside
// the "Become a Mentor" confirmation dialog. The dialog is closed by default,
// but a static import was pulling the icon onto every homepage load and
// Lighthouse flagged it as 15 KiB unused JS on the homepage chunk. Lazy-load
// with webpack hints so Next doesn't pre-emptively warm the chunk either.
const OnboardingMentor = dynamic(
  () => import(/* webpackPrefetch: false, webpackPreload: false */ '@ui/components/icons/OnboardingMentor'),
  { ssr: false, loading: () => null }
);

interface PromotionSlide {
  title: string;
  description: string;
  button: string;
}

interface PromotionSectionClientWrapperProps {
  promotions: PromotionSlide[];
  locale: string;
}

/**
 * Client component wrapper for PromotionSection.
 * Handles client-side interactions like navigation and role switching.
 */
export const PromotionSectionClientWrapper: React.FC<PromotionSectionClientWrapperProps> = ({ promotions, locale }) => {
  const { data: user } = useCurrentUser();
  const router = useRouter();
  const t = useTranslations('Profile');
  const [isMentorModalOpen, setIsMentorModalOpen] = useState(false);
  const { becomeMentor } = useBecomeMentor(locale, { onSettled: () => setIsMentorModalOpen(false) });

  const handleConfirmBecomeMentor = () => {
    if (!user) {
      return;
    }
    becomeMentor(user.id);
  };

  // Update to handle the promotion ID parameter
  const handleButtonClick = (_promotionId: string) => {
    if (!user) {
      // Anonymous visitors land on the pitch page first; its CTA carries the
      // intent through login so they don't have to click "become a mentor" twice.
      router.push(MENTOR_LANDING_PATHS[locale as SiteLocale] ?? MENTOR_LANDING_PATHS.fr);
      return;
    }

    // Show confirmation dialog (same as role switch in header)
    setIsMentorModalOpen(true);
  };

  // Don't show "become a mentor" promotions to professionals (they're being redirected to dashboard)
  if (user?.isProfessional) {
    return null;
  }

  // Transform the promotions array to match PromotionSection's expected format
  // Assign different images to each slide
  const transformedPromotions = promotions.map((promo, index) => {
    let imageUrl;

    // First slide uses default image, second and third use the new images
    if (index === 1) {
      imageUrl = bannerImage2;
    } else if (index === 2) {
      imageUrl = bannerImage3;
    }
    // index === 0 will use the default image (become_a_pro.png) from PromotionBanner

    return {
      id: `promo-${index + 1}`,
      title: promo.title,
      description: promo.description,
      buttonLabel: promo.button,
      ...(imageUrl && { imageUrl })
    };
  });

  return (
    <>
      <PromotionSection promotions={transformedPromotions} onButtonClick={handleButtonClick} />

      {/* Become Mentor Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isMentorModalOpen}
        onClose={() => setIsMentorModalOpen(false)}
        onConfirm={handleConfirmBecomeMentor}
        onReject={() => setIsMentorModalOpen(false)}
        title={t('becomeMentorTitle')}
        description={t('becomeMentorDescription')}
        confirmButtonText={t('yes')}
        rejectButtonText={t('noStayLearner')}
        icon={<OnboardingMentor className="size-20" />}
      />
    </>
  );
};
